/**
 * ログインページ - melta UI
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Icon } from '../components/icons';
import { Button, Card, Input, Alert } from '../components/ui';

export default function Login() {
  const { login, setNewPassword, pendingUser, error } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPasswordVal] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      }
    } catch (err) {
      setLocalError(err.message || 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('パスワードが一致しません');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('パスワードは8文字以上で入力してください');
      return;
    }

    setIsLoading(true);
    try {
      const result = await setNewPassword(newPassword);
      if (result.success) {
        navigate('/');
      }
    } catch (err) {
      setLocalError(err.message || 'パスワード変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 初回パスワード変更画面
  if (pendingUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="key" className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">パスワード変更</h1>
            <p className="text-sm text-slate-500 mt-2">
              初回ログインのため、新しいパスワードを設定してください
            </p>
          </div>

          <form onSubmit={handleNewPassword} className="space-y-5">
            {(localError || error) && (
              <Alert variant="error">
                {localError || error}
              </Alert>
            )}

            <Input
              label="新しいパスワード"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPasswordVal(e.target.value)}
              placeholder="8文字以上、大小英字・数字含む"
              required
              icon={<Icon name="lock" className="w-5 h-5" />}
            />

            <Input
              label="パスワード確認"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="パスワード再入力"
              required
              icon={<Icon name="lock" className="w-5 h-5" />}
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              loading={isLoading}
            >
              パスワードを変更
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // 通常ログイン画面
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="wifi" className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">IoT Device Management</h1>
          <p className="text-sm text-slate-500 mt-2">
            機体管理コンソールにログイン
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          {(localError || error) && (
            <Alert variant="error">
              {localError || error}
            </Alert>
          )}

          <Input
            label="メールアドレス"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            required
            autoFocus
            icon={<Icon name="mail" className="w-5 h-5" />}
          />

          <Input
            label="パスワード"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="パスワード"
            required
            icon={<Icon name="lock" className="w-5 h-5" />}
          />

          <Button
            type="submit"
            className="w-full"
            size="lg"
            loading={isLoading}
          >
            ログイン
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500">
          アカウントをお持ちでない方は{' '}
          <Link to="/register" className="text-primary-500 hover:text-primary-700 font-medium">
            新規登録
          </Link>
        </div>
      </Card>
    </div>
  );
}
